import React from "react"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"

const outcomeIcon = (outcome) => {
  if (outcome === "ok") return { symbol: "✓", className: "text-green-600", label: "ok" }
  return { symbol: "⚠", className: "text-amber-600", label: "errored" }
}

export default function ResultTable({ summary, results }) {
  const { t } = useTranslation()

  const finalizeLabel = (finalize) => {
    if (!finalize || !finalize.outcome) return t("natura_operations.deposit_transfer.finalize_none")
    if (finalize.outcome === "finalized") {
      // LOST-only modes report units_discarded; REM reports a shipped order.
      if (finalize.units_discarded != null) {
        return t("natura_operations.deposit_transfer.finalize_discarded", { count: finalize.units_discarded })
      }
      return t("natura_operations.deposit_transfer.finalize_finalized")
    }
    if (finalize.outcome === "skipped")
      return t("natura_operations.deposit_transfer.finalize_skipped", { reason: finalize.reason || "" })
    return t("natura_operations.deposit_transfer.finalize_errored")
  }

  return (
    <div className="mt-6" data-testid="deposit-transfer-result">
      <h2 className="font-semibold mb-2">{t("natura_operations.deposit_transfer.step4_heading")}</h2>

      <div className="mb-3 text-sm text-gray-700" data-testid="dt-result-summary">
        {t("natura_operations.deposit_transfer.result_summary", {
          total: summary?.total ?? results.length,
          ok: summary?.ok ?? 0,
          errored: summary?.errored ?? 0,
          finalized: summary?.finalized ?? 0,
          units_discarded: summary?.units_discarded ?? 0,
        })}
      </div>

      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 text-left">
            <tr>
              <th className="px-3 py-2 w-8" aria-label="icon" />
              <th className="px-3 py-2 font-medium">{t("natura_operations.deposit_transfer.col_sku")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.deposit_transfer.col_lot")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.deposit_transfer.col_quantity")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.deposit_transfer.col_status")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.deposit_transfer.col_message")}</th>
              <th className="px-3 py-2 font-medium">{t("natura_operations.deposit_transfer.col_finalize")}</th>
            </tr>
          </thead>
          <tbody>
            {results.map((row, i) => {
              const icon = outcomeIcon(row.outcome)
              const statusLabel =
                row.outcome === "ok"
                  ? t("natura_operations.deposit_transfer.outcome_ok")
                  : t("natura_operations.deposit_transfer.outcome_errored")
              return (
                <tr
                  key={row.lot_id ?? i}
                  className="border-t border-gray-100"
                  data-testid={`dt-result-row-${row.lot_id ?? i}`}
                >
                  <td className={`px-3 py-2 text-center font-bold ${icon.className}`} aria-label={icon.label}>
                    {icon.symbol}
                  </td>
                  <td className="px-3 py-2 font-mono">{row.sku ?? "—"}</td>
                  <td className="px-3 py-2 font-mono">{row.lot_code ?? "—"}</td>
                  <td className="px-3 py-2">{row.quantity ?? 0}</td>
                  <td className="px-3 py-2">{statusLabel}</td>
                  <td className="px-3 py-2 break-words max-w-xs">{row.message ?? "—"}</td>
                  <td className="px-3 py-2" data-testid={`dt-result-finalize-${row.lot_id ?? i}`}>
                    {finalizeLabel(row.finalize)}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

ResultTable.propTypes = {
  summary: PropTypes.shape({
    total: PropTypes.number,
    ok: PropTypes.number,
    errored: PropTypes.number,
    finalized: PropTypes.number,
    units_discarded: PropTypes.number,
  }),
  results: PropTypes.arrayOf(PropTypes.object).isRequired,
}

ResultTable.defaultProps = {
  summary: null,
}
